/** Types shared by the Projects & Tasks UI data layer. */

export const TASK_STATUSES = [
  "open",
  "in_progress",
  "on_hold",
  "done",
  "cancelled",
] as const;

export type TaskStatus = (typeof TASK_STATUSES)[number];

export interface ProjectWikiLink {
  wiki_path: string;
  role: "primary" | "reference";
}

/** Project task artifacts are intentionally schema-flexible on the API. */
export type Artifact = Record<string, unknown>;

export interface Project {
  id: string;
  name: string;
  directive: string;
  default_agent_id: string;
  is_private: boolean;
  allowed_roles: string[] | null;
  created_by: string;
  created_at: string;
  metadata: Record<string, unknown>;
  wiki_links: ProjectWikiLink[];
  is_pinned: boolean;
}

export interface ProjectListResponse {
  projects: Project[];
  has_more: boolean;
  next_cursor: string | null;
}

export type AccessibleProjectVisibility = "Private" | "Shared" | "Role restricted";

/** Summary returned by the all-accessible-projects catalog endpoint. */
export interface AccessibleProjectSummary {
  id: string;
  name: string;
  description_preview: string;
  created_by: string;
  created_at: string;
  last_activity_at: string;
  session_count: number;
  open_task_count: number;
  visibility: AccessibleProjectVisibility;
  is_pinned: boolean;
}

export interface AccessibleProjectListResponse {
  projects: AccessibleProjectSummary[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface ProjectCreateRequest {
  name: string;
  directive?: string;
  default_agent_id: string;
  is_private?: boolean;
  allowed_roles?: string[] | null;
  reference_links?: string[];
}

export interface ProjectUpdateRequest {
  name?: string;
  directive?: string;
  default_agent_id?: string;
  is_private?: boolean;
  allowed_roles?: string[] | null;
}

export interface Tag {
  id: string;
  project_id: string;
  name: string;
  color: string | null;
  is_archived: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface TagListResponse {
  tags: Tag[];
}

export interface TagCreateRequest {
  name: string;
  color?: string | null;
}

export interface TagUpdateRequest {
  name?: string;
  color?: string | null;
  is_archived?: boolean;
}

export interface Task {
  id: string;
  project_id: string;
  session_id: string | null;
  assigned_to: string | null;
  title: string;
  /** Task-created reference material shown separately from chat messages. */
  description: string | null;
  status: TaskStatus;
  artifacts: Artifact[];
  tags: Tag[];
  created_by: string;
  created_at: string;
  updated_at: string;
  /** Calendar date; absent on responses from older servers. */
  due_date?: string | null;
  parent_task_id?: string | null;
  direct_subtask_count?: number;
  direct_subtask_done_count?: number;
  direct_subtask_open_count?: number;
  descendant_count?: number;
  descendant_done_count?: number;
  descendant_open_count?: number;
  ancestor_path?: TaskAncestor[];
  effective_tags?: EffectiveTaskTag[];
}

export interface TaskAncestor { id: string; title: string; session_id: string | null }
export interface EffectiveTaskTag { tag: Tag; inherited: boolean; source_task: { id: string; title: string } | null }
export interface SubtasksResponse {
  tasks: Task[];
  has_more: boolean;
  next_cursor: string | null;
  direct_subtask_count: number;
  direct_subtask_done_count: number;
  direct_subtask_open_count: number;
  descendant_count: number;
  descendant_done_count: number;
  descendant_open_count: number;
}

export interface TaskListResponse {
  tasks: Task[];
  has_more: boolean;
  next_cursor: string | null;
  due_date_supported?: boolean;
  tag_filter_supported?: boolean;
}

export type TaskTitleSource = "default" | "user";

export interface TaskCreateRequest {
  title: string;
  title_source?: TaskTitleSource;
  description?: string | null;
  status?: TaskStatus;
  session_id?: string | null;
  assigned_to?: string | null;
  tags?: string[];
  due_date?: string | null;
  parent_task_id?: string | null;
}

export interface TaskUpdateRequest {
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  assigned_to?: string | null;
  tags?: string[];
  due_date?: string | null;
}

export interface TenantUser {
  id: string;
  display_name?: string;
  email?: string;
  role?: string;
}
